import React, { useEffect, useRef, useState } from "react";
import { Col, Form, Row, Table, Dropdown, Pagination } from "react-bootstrap";
import { Common, EmployeeIcons, SettingsIcons } from "../../assets/images";
import { useNavigate } from "react-router-dom";
// Redux Imports
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  setShowRecruitAgencyExportDropdown,
  setRecruitAgencyListPageSize,
  setRecruitAgencyListPageNo,
  setFilterRecruitAgencyPageNo,
  setFilterRecruitAgencyPageSize,
} from "../../store/slices/campsSlice";
import { setRecruitAgencyChange } from "../../store/slices/commonSlice";
import { LoaderBlue } from "../Lotties";
// API Imports
import { useDeleteRecruitAgencyMutation } from "../../services/Camps";
import { RecruitAgency } from "../../interfaces/employee";
import { exportToCsv, exportToPDF } from "../../config";
import RecruitAgencyFilterModal, {
  FormProps,
} from "../Modals/recruit-agency-filter-modal";
import Content from "./get-recruit-agency-api";
import { useReactToPrint } from "react-to-print";
import SelectSearch from "react-select-search";

const AllRecruitAgencies = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const tableRef = useRef(null);
  const [print, setPrint] = useState(false);
  const handlePrint = useReactToPrint({
    content: () => tableRef.current,
    onAfterPrint: () => setPrint(false),
  });
  const handlePrinted = async () => {
    await setPrint(true);
    handlePrint();
  };
  const state = useAppSelector((state) => state.camps);
  const accessToken = useAppSelector((state) => state.common.accessToken);
  const userID = useAppSelector((state) => state.common.userID);
  const profileData = useAppSelector((state) => state.settings.profileData);
  const [deleteLoadingMap, setDeleteLoadingMap] = useState<{
    [key: string]: boolean;
  }>({});
  const recruitAgencyChange = useAppSelector(
    (state) => state.common.recruitAgencyChange,
  );
  const recruiterIds = useAppSelector((state) => state.camps.recruitAgencyIds);
  // Options
  const options = recruiterIds.map((item) => ({
    name: item.recruiterId + " - " + item.recruiterName,
    value: item.recruiterId,
  }));
  // Pagination && Filter
  const [filterInput, setFilterInput] = useState<FormProps>({
    licenseNumber: "",
  });
  const [filterSearch, setFilterSearch] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [filteredData, setFilteredData] = useState<RecruitAgency[]>([]);
  const totalPages = Math.ceil(
    state.recruitAgencyListTotalPages / state.recruitAgencyListPageSize,
  );
  const totalCount = filterSearch
    ? state.filterRecruitAgencyTotalPages
    : state.recruitAgencyListTotalPages;

  const filterTotalPages = Math.ceil(
    state.filterRecruitAgencyTotalPages / state.filterRecruitAgencyPageSize,
  );

  // show 5 pages at a time in pagination bar and rest will be shown in next and previous
  const showPages = 5;
  const startPage =
    Math.floor((state.recruitAgencyListPageNo - 1) / showPages) * showPages;
  const endPage = startPage + showPages;
  const pages = [...Array(totalPages).keys()].slice(startPage, endPage);
  const filterStartPage =
    Math.floor((state.filterRecruitAgencyPageNo - 1) / showPages) * showPages;
  const filterEndPage = filterStartPage + showPages;
  const filterPages = [...Array(filterTotalPages).keys()].slice(
    filterStartPage,
    filterEndPage,
  );
  // handle go to page number that is after the 4 pages of current page
  const handleGoToNextPages = () => {
    //   if it is in the last 5 pages then dont do anything
    if (state.recruitAgencyListPageNo > totalPages - showPages) {
      return;
    }
    dispatch(
      setRecruitAgencyListPageNo(state.recruitAgencyListPageNo + showPages),
    );
  };

  const handleGoToPreviousPages = () => {
    //   if it is in the first 5 pages then dont do anything
    if (state.recruitAgencyListPageNo <= 5) {
      return;
    }
    dispatch(
      setRecruitAgencyListPageNo(state.recruitAgencyListPageNo - showPages),
    );
  };

  const handleFilterGoToNextPages = () => {
    //   if it is in the last 5 pages then dont do anything
    if (state.filterRecruitAgencyPageNo > filterTotalPages - showPages) {
      return;
    }
    dispatch(
      setFilterRecruitAgencyPageNo(state.filterRecruitAgencyPageNo + showPages),
    );
  };

  const handleFilterGoToPreviousPages = () => {
    //   if it is in the first 5 pages then dont do anything
    if (state.filterRecruitAgencyPageNo <= 5) {
      return;
    }
    dispatch(
      setFilterRecruitAgencyPageNo(state.filterRecruitAgencyPageNo - showPages),
    );
  };

  const handlePageChange = (page: any) => {
    dispatch(setRecruitAgencyListPageNo(page));
  };

  const handleFilterPageChange = (page: any) => {
    dispatch(setFilterRecruitAgencyPageNo(page));
  };

  const handlePageSizeChange = (event: any) => {
    const selectedPageSize = parseInt(event.target.value, 10); // Parse the value to a number
    if (filterSearch) {
      dispatch(setFilterRecruitAgencyPageSize(selectedPageSize));
    } else {
      dispatch(setRecruitAgencyListPageSize(selectedPageSize));
    }
  };
  useEffect(() => {
    setFilteredData(state.recruitAgencyList);
  }, [state.recruitAgencyList]);

  // Employees List
  const tableData = {
    tableHeading: [
      "Sr. No.",
      "Recruiter Id.",
      "Recruiter Name",
      "Company Name",
      "License Number",
      "Country",
      "Contact Person Name",
      "Contact Person Designation",
      "Contact Person Phone",
      "Actions",
    ],
    tableData: filteredData,
  };

  const csvData = tableData.tableData.map((item, index) => ({
    "Sr. No.": index + 1,
    "Recruiter Id.": item.recruiterId,
    "Recruiter Name": item.recruiterName,
    "Company Name": item.companyName,
    "License Number": item.licenseNumber,
    Country: item.country,
    "Contact Person Name": item.contactPersonName,
    "Contact Person Designation": item.contactPersonDesignation,
    "Contact Person Phone": item.contactPersonPhone,
  }));

  const [deleteRecruitAgency] = useDeleteRecruitAgencyMutation();

  const handleDelete = async (empId: string) => {
    setDeleteLoadingMap((prevMap) => ({ ...prevMap, [empId]: true }));
    try {
      const data = {
        userID: userID || "",
        accessToken: accessToken || "",
        permission: profileData?.permissions.deleteLegal || false,
        payload: {
          recruiterId: empId,
        },
      };
      await deleteRecruitAgency(data).unwrap();
      dispatch(setRecruitAgencyChange(!recruitAgencyChange));
    } catch (e) {
      console.log(e);
    } finally {
      setDeleteLoadingMap((prevMap) => ({ ...prevMap, [empId]: false }));
    }
  };

  const handleReset = () => {
    setQuery("");
    setSearch("");
    setFilteredData(state.recruitAgencyList);
    setFilterSearch(false);
    setFilterInput({
      licenseNumber: "",
    });
  };

  const twoWords = (name: string) => {
    const words = name.split(" ");
    return words[0] + " " + words[1];
  };

  return (
    <div className="user-list mt-2 section px-4 py-4">
      <Content />
      <RecruitAgencyFilterModal
        show={showFilterModal}
        setShow={setShowFilterModal}
        setRecruitAgencies={setFilteredData}
        filterSearch={filterSearch}
        setFilterSearch={setFilterSearch}
        recruitAgencies={state.recruitAgencyList}
        query={query}
        filterInput={filterInput}
        setFilterInput={setFilterInput}
      />
      <Row>
        <Col sm="12">
          <div className="section-heading ">
            <h1 className="heading mb-0">Recruitment Agencies</h1>
            <div className="d-flex align-items-center justify-content-between mt-4">
              <div
                className=" d-flex align-items-center gap-2"
                style={{ width: "25%" }}
              >
                <Form.Group className="w-100" controlId="formBasicEmail">
                  <Form.Group className="position-relative input-design d-flex align-items-center">
                    <SelectSearch
                      options={options}
                      onChange={(value: any) => {
                        setQuery(value);
                        setFilterSearch(true);
                      }}
                      value={query}
                      search={true}
                      placeholder="Search Recruiter by Id/Name "
                    />
                    <img src={Common.ArrowDown} alt="message" />
                  </Form.Group>
                </Form.Group>
                {query !== "" ? (
                  <button
                    type="button"
                    className="btn primary-button w-25"
                    onClick={handleReset}
                  >
                    {" "}
                    Reset
                  </button>
                ) : filterSearch ? (
                  <button
                    type="button"
                    className="btn primary-button w-25"
                    onClick={handleReset}
                  >
                    {" "}
                    Reset
                  </button>
                ) : null}
              </div>
              <div className="d-flex align-items-center gap-4">
                <button
                  className="dropdown-toggle-white dropdown-toggle-blue"
                  type="button"
                  onClick={() => navigate("/recruit-agency/add-recruit-agency")}
                >
                  <img src={Common.Plus} height="22px" width="22px" alt="img" />{" "}
                  New
                </button>
                <button
                  id="dropdown-basic"
                  onClick={() => {
                    setShowFilterModal(true);
                  }}
                  className="dropdown-toggle-white dropdown-toggle-blue"
                >
                  Filter{" "}
                </button>
                <Dropdown
                  show={state.showRecruitAgencyExportDropdown}
                  className="position-relative"
                >
                  <button
                    id="dropdown-basic"
                    onClick={() => {
                      dispatch(
                        setShowRecruitAgencyExportDropdown(
                          !state.showRecruitAgencyExportDropdown,
                        ),
                      );
                    }}
                    className=" bg-transparent dropdown-toggle-white"
                  >
                    Export{" "}
                    <img
                      src={EmployeeIcons.ArrowDownBlue}
                      className="ms-2"
                      alt="down-arrow"
                    />
                  </button>
                  <Dropdown.Menu
                    className="mt-1 custom-scrollbar"
                    show={state.showRecruitAgencyExportDropdown}
                  >
                    <Dropdown.Item
                      onClick={() => {
                        exportToCsv(csvData, "recruiters-list");
                        dispatch(setShowRecruitAgencyExportDropdown(false));
                      }}
                    >
                      CSV
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => {
                        dispatch(setShowRecruitAgencyExportDropdown(false));
                        handlePrinted();
                      }}
                    >
                      PDF
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
          </div>
        </Col>
        <Col sm="12">
          {state.recruitAgencyListLoading ? (
            <LoaderBlue height={70} width={70} />
          ) : profileData.permissions.showLegal ? (
            <>
              {tableData.tableData?.length > 0 ? (
                <div className="p-3" ref={tableRef}>
                  {print && (
                    <>
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <img src={Common.Vision} width="110px" alt="logo" />
                        </div>
                        <div className="letter-logo text-center gap-2">
                          <img src={Common.LogoSVG} width="80px" alt="logo" />
                          <div className="mt-1">
                            <h3
                              className="logo-text mb-0"
                              style={{
                                color: "#202020",
                                fontSize: "10px",
                                fontWeight: "700",
                              }}
                            >
                              Senyar Arabian Trading Co. LTD
                            </h3>
                            <h3
                              className="logo-text logo-arabic mb-0"
                              style={{
                                color: "#202020",
                                fontSize: "13px",
                                fontWeight: "700",
                              }}
                            >
                              شركة سنيار العربية التجارية المحدودة
                            </h3>
                          </div>
                        </div>
                      </div>
                      <h1 className="sub-heading text-center mt-3">
                        Recruiters List
                      </h1>
                    </>
                  )}
                  <Table
                    striped
                    bordered
                    responsive
                    className={
                      print
                        ? "mt-5 text-center print-table"
                        : "mt-5 text-center"
                    }
                  >
                    <thead>
                      <tr className="border-0">
                        {tableData.tableHeading.map((item, index) => (
                          <th
                            className={
                              item === "Actions" && print
                                ? "border-0 d-none"
                                : "border-0"
                            }
                            key={index}
                          >
                            {item}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.tableData.map((item, index) => (
                        <tr
                          key={index}
                          style={{ borderColor: "rgba(32, 32, 32, 0.20)" }}
                        >
                          <td>
                            {totalCount > 0
                              ? filterSearch
                                ? (state.filterRecruitAgencyPageNo - 1) *
                                    state.filterRecruitAgencyPageSize +
                                  index +
                                  1
                                : (state.recruitAgencyListPageNo - 1) *
                                    state.recruitAgencyListPageSize +
                                  index +
                                  1
                              : index + 1}
                          </td>
                          <td
                            className="cursor-pointer"
                            onClick={() =>
                              navigate(
                                `/recruit-agency/view-recruit-agency/${item.recruiterId}`,
                                {
                                  state: { data: item },
                                },
                              )
                            }
                          >
                            {item.recruiterId}
                          </td>
                          <td
                            className="cursor-pointer"
                            onClick={() =>
                              navigate(
                                `/recruit-agency/view-recruit-agency/${item.recruiterId}`,
                                {
                                  state: { data: item },
                                },
                              )
                            }
                          >
                            {print
                              ? twoWords(item.recruiterName)
                              : item.recruiterName}
                          </td>
                          <td>{item.companyName}</td>
                          <td>{item.licenseNumber}</td>
                          <td>{item.country}</td>
                          <td>{item.contactPersonName}</td>
                          <td>{item.contactPersonDesignation}</td>
                          <td>{item.contactPersonPhone}</td>
                          {!print && (
                            <td>
                              <button
                                className="btn p-0 border-0 me-2"
                                onClick={() =>
                                  navigate(
                                    `/recruit-agency/view-recruit-agency/${item.recruiterId}`,
                                    {
                                      state: { data: item },
                                    },
                                  )
                                }
                              >
                                <img
                                  src={Common.Eye}
                                  width="30px"
                                  height="30px"
                                  alt="edit-user"
                                />
                              </button>
                              <button
                                className="btn p-0 border-0 me-2"
                                onClick={() =>
                                  navigate(
                                    `/recruit-agency/edit-recruit-agency/${item.recruiterId}`,
                                    {
                                      state: { data: item },
                                    },
                                  )
                                }
                              >
                                <img
                                  src={SettingsIcons.EditUser}
                                  width="30px"
                                  height="30px"
                                  alt="edit-user"
                                />
                              </button>
                              {deleteLoadingMap[item.recruiterId] ? (
                                <LoaderBlue height={20} width={20} />
                              ) : (
                                <button
                                  className="btn p-0 border-0"
                                  onClick={() => handleDelete(item.recruiterId)}
                                >
                                  <img
                                    src={SettingsIcons.Delete}
                                    width="24px"
                                    height="24px"
                                    alt="edit-user"
                                  />
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center mt-5">
                  <h1 className="heading">No recruiters found</h1>
                </div>
              )}
            </>
          ) : (
            <div className="text-center mt-5">
              <h1 className="heading">Unauthorized Access</h1>
            </div>
          )}
        </Col>
        {filteredData.length > 0 && (
          <Col sm="12">
            <div className="mt-3 d-flex justify-content-between align-items-center">
              <Form.Group
                className="d-flex align-items-center gap-2"
                controlId="formBasicEmail"
              >
                <h4
                  className="sub-heading normal-font-weight textBlack"
                  style={{ fontSize: "14px", width: "230px" }}
                >
                  Entries per page
                </h4>
                <Form.Select
                  name="pageSize"
                  onChange={handlePageSizeChange}
                  value={
                    filterSearch
                      ? state.filterRecruitAgencyPageSize.toString()
                      : state.recruitAgencyListPageSize.toString()
                  }
                  className="custom-input border-fill custom-height"
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="100">100</option>
                  <option value="10000">All</option>
                </Form.Select>
              </Form.Group>
              <div className="pagination-custom">
                <Pagination className="justify-content-end">
                  <Pagination.Item
                    onClick={() =>
                      filterSearch
                        ? handleFilterGoToPreviousPages()
                        : handleGoToPreviousPages()
                    }
                  >
                    <img src={Common.DoubleArrowLeft} alt="arrow" />
                  </Pagination.Item>
                  <Pagination.Item
                    onClick={() =>
                      filterSearch
                        ? handleFilterPageChange(
                            state.filterRecruitAgencyPageNo > 1
                              ? state.filterRecruitAgencyPageNo - 1
                              : 1,
                          )
                        : handlePageChange(
                            state.recruitAgencyListPageNo > 1
                              ? state.recruitAgencyListPageNo - 1
                              : 1,
                          )
                    }
                  >
                    <img
                      src={EmployeeIcons.ArrowDownBlue}
                      className="rotate-left"
                      alt="arrow"
                    />
                  </Pagination.Item>
                  {filterSearch ? (
                    <>
                      {/* render filter pages here */}
                      {filterPages.map((page, index) => (
                        <Pagination.Item
                          key={index}
                          onClick={() => handleFilterPageChange(page + 1)}
                          className={
                            page + 1 === state.filterRecruitAgencyPageNo
                              ? "active-page"
                              : ""
                          }
                        >
                          {page + 1}
                        </Pagination.Item>
                      ))}
                    </>
                  ) : (
                    <>
                      {/* render pages here */}
                      {pages.map((page, index) => (
                        <Pagination.Item
                          key={index}
                          onClick={() => handlePageChange(page + 1)}
                          className={
                            page + 1 === state.recruitAgencyListPageNo
                              ? "active-page"
                              : ""
                          }
                        >
                          {page + 1}
                        </Pagination.Item>
                      ))}
                    </>
                  )}
                  <Pagination.Item
                    onClick={() =>
                      filterSearch
                        ? handleFilterPageChange(
                            state.filterRecruitAgencyPageNo < filterTotalPages
                              ? state.filterRecruitAgencyPageNo + 1
                              : filterTotalPages,
                          )
                        : handlePageChange(
                            state.recruitAgencyListPageNo < totalPages
                              ? state.recruitAgencyListPageNo + 1
                              : totalPages,
                          )
                    }
                  >
                    <img
                      src={EmployeeIcons.ArrowDownBlue}
                      className="rotate-right"
                      alt="arrow"
                    />
                  </Pagination.Item>
                  <Pagination.Item
                    onClick={() => {
                      filterSearch
                        ? handleFilterGoToNextPages()
                        : handleGoToNextPages();
                    }}
                  >
                    <img src={Common.DoubleArrowRight} alt="arrow" />
                  </Pagination.Item>
                </Pagination>
              </div>
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
};
export default AllRecruitAgencies;
